import { useState } from "react";
import {
  Sparkles, Loader2, AlertCircle, CheckCircle2, AlertTriangle, Ban,
  ThumbsUp, PauseCircle, XCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { useTradingStyle } from "@/contexts/TradingStyleContext";

/**
 * Dual-provider AI Analyst (Phase I12).
 *
 * Backend returns:
 *   { snapshot_id, providers: { openai: {...}, moonshot: {...} }, agreement: {...} }
 * Renders one card per provider side-by-side, and an agreement strip above.
 *
 * Honesty rules from I10 apply — this is commentary, not a validated edge.
 * The `stance` from each model is a machine-comparable label, not a
 * predictive score; disagreement is INFORMATION, not an error.
 */

const STANCE_META = {
  favor_entry: { label: "Favor entry", cls: "text-emerald-300 border-emerald-500/50 bg-emerald-500/10", Icon: ThumbsUp },
  wait:        { label: "Wait",        cls: "text-amber-300 border-amber-500/50 bg-amber-500/10",     Icon: PauseCircle },
  avoid:       { label: "Avoid",       cls: "text-red-300 border-red-500/50 bg-red-500/10",           Icon: XCircle },
};

const VERDICT_META = {
  agree:    { cls: "border-emerald-500/50 bg-emerald-500/10 text-emerald-200", Icon: CheckCircle2 },
  partial:  { cls: "border-amber-500/50  bg-amber-500/10  text-amber-100",     Icon: AlertTriangle },
  disagree: { cls: "border-orange-500/60 bg-orange-500/15 text-orange-100",    Icon: AlertTriangle },
  unknown:  { cls: "border-slate-600     bg-slate-800/40  text-slate-300",     Icon: AlertCircle },
};

export default function AIAnalystPanel({ ticker }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const { style } = useTradingStyle() || {};
  const mode = (style || "SWING").toLowerCase();

  const gen = async (force = false) => {
    setLoading(true); setErr(null);
    try {
      const params = { mode, ...(force ? { force: true } : {}) };
      const r = await api.post(`/signal/ai/${ticker}`, null, { params });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  const openai   = data?.providers?.openai;
  const moonshot = data?.providers?.moonshot;
  const agreement = data?.agreement;

  return (
    <div data-testid="ai-analyst-panel" className="border border-[#1E293B] bg-[#0F1626]">
      <div className="px-3 py-2 border-b border-[#1E293B] bg-[#0B111C] flex items-center gap-2 flex-wrap">
        <Sparkles className="h-3.5 w-3.5 text-cyan-400" />
        <span className="mono text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
          Dual AI Analyst
        </span>
        <span
          data-testid="ai-mode-badge"
          data-mode={mode}
          className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-cyan-500/40 bg-cyan-500/10 text-cyan-200"
        >
          {mode} snapshot
        </span>
        <span className="mono text-[9px] uppercase tracking-widest text-slate-600">
          Commentary · No quant mutation · Descriptive per I10
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button
            data-testid="ai-generate"
            onClick={() => gen(!!data)}
            disabled={loading}
            className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-cyan-500/40 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-200 disabled:opacity-50 flex items-center gap-1"
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
            {data ? "Regenerate" : "Generate analysis"}
          </button>
        </div>
      </div>
      <div className="p-3 space-y-3">
        {!data && !loading && (
          <div className="mono text-[11px] text-slate-500 text-center py-4">
            Click "Generate analysis" to run both AI analysts (OpenAI + Moonshot Kimi) in parallel on the deterministic signal payload.
          </div>
        )}
        {loading && (
          <div className="mono text-[11px] text-slate-500 text-center py-4 flex items-center justify-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Reasoning… both providers running in parallel (Kimi K3 typically takes ~40-60s).
          </div>
        )}
        {err && !loading && (
          <div className="mono text-[11px] text-red-200 border border-red-500/30 bg-red-500/10 px-3 py-2 flex items-start gap-2" data-testid="ai-panel-error">
            <AlertCircle className="h-3.5 w-3.5 text-red-400 shrink-0 mt-0.5" />
            Panel error — {err}
          </div>
        )}

        {agreement && (
          <AgreementStrip agreement={agreement} />
        )}

        {(openai || moonshot) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <AnalystCard provider={openai}   testid="ai-card-openai"   />
            <AnalystCard provider={moonshot} testid="ai-card-moonshot" />
          </div>
        )}
      </div>
    </div>
  );
}

function AgreementStrip({ agreement }) {
  const meta = VERDICT_META[agreement.verdict] || VERDICT_META.unknown;
  const Icon = meta.Icon;
  return (
    <div
      data-testid="ai-agreement-strip"
      data-verdict={agreement.verdict}
      className={`border ${meta.cls} px-3 py-2 flex items-start gap-2`}
    >
      <Icon className="h-4 w-4 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="mono text-[10px] uppercase tracking-widest opacity-70">Agreement Summary</div>
        <div className="mono text-[12px] font-bold mt-0.5">{agreement.headline}</div>
        <div className="mono text-[10px] opacity-80 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>
            Risk-theme overlap:
            <span className="tabular-nums font-bold ml-1">{agreement.risk_overlap_pct}%</span>
          </span>
          {agreement.shared_risk_keywords?.length > 0 && (
            <span>
              Shared:{" "}
              <span className="opacity-90">{agreement.shared_risk_keywords.join(", ")}</span>
            </span>
          )}
          <span className="opacity-60 italic">{agreement.note}</span>
        </div>
      </div>
    </div>
  );
}

function StancePill({ stance }) {
  const meta = STANCE_META[stance];
  if (!meta) return null;
  const Icon = meta.Icon;
  return (
    <span
      data-testid={`ai-stance-${stance}`}
      className={`mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${meta.cls} inline-flex items-center gap-1`}
    >
      <Icon className="h-2.5 w-2.5" />
      {meta.label}
    </span>
  );
}

function AnalystCard({ provider, testid }) {
  if (!provider) return null;
  const displayName = provider.provider_display
    || (provider.provider === "openai" ? "OpenAI" : "Moonshot Kimi");
  const model = provider.ai_model;
  if (!provider.available) {
    return (
      <div
        data-testid={testid}
        data-status="unavailable"
        className="border border-[#1E293B] bg-[#0B111C]"
      >
        <CardHeader displayName={displayName} model={model} cached={false} />
        <div className="px-3 py-4 mono text-[11px] text-slate-400 border-t border-[#1E293B] flex items-start gap-2">
          <Ban className="h-3.5 w-3.5 text-slate-500 shrink-0 mt-0.5" />
          <div>
            <div className="font-bold text-slate-300">Unavailable</div>
            <div className="text-slate-500 mt-0.5">{provider.reason || "no reason returned"}</div>
            <div className="text-slate-600 text-[10px] mt-1">
              The other provider's analysis is still shown independently.
            </div>
          </div>
        </div>
      </div>
    );
  }
  const a = provider.analysis;
  return (
    <div
      data-testid={testid}
      data-status="ok"
      data-provider={provider.provider}
      className="border border-[#1E293B] bg-[#0B111C]"
    >
      <CardHeader
        displayName={displayName}
        model={model}
        cached={provider.cached}
        latency={provider.latency_s}
        stance={a.stance}
      />
      <div className="p-3 space-y-3 border-t border-[#1E293B]">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-cyan-400 mb-1">Summary</div>
          <p className="text-slate-200 text-[13px] leading-relaxed" data-testid={`${testid}-summary`}>
            {a.summary}
          </p>
        </div>
        <div className="grid grid-cols-1 gap-2">
          <FactorBlock title="Bullish"   items={a.bullish_factors}     tone="emerald" testid={`${testid}-bullish`}   />
          <FactorBlock title="Bearish"   items={a.bearish_factors}     tone="red"     testid={`${testid}-bearish`}   />
          <FactorBlock title="Risks"     items={a.risks}               tone="amber"   testid={`${testid}-risks`}     />
          <FactorBlock title="Conflicts" items={a.conflicting_signals} tone="slate"   testid={`${testid}-conflicts`} />
        </div>
        <div className="grid grid-cols-1 gap-2 pt-2 border-t border-[#151C2C]">
          <TextBlock title="Entry Assessment"       body={a.entry_assessment}       testid={`${testid}-entry`}         />
          <TextBlock title="Invalidation Reasoning" body={a.invalidation_reasoning} testid={`${testid}-invalidation`}  />
          <TextBlock title="Confidence Commentary"  body={a.confidence_commentary}  testid={`${testid}-confidence`}    />
        </div>
      </div>
    </div>
  );
}

function CardHeader({ displayName, model, cached, latency, stance }) {
  // model comes from backend as e.g. "openai/gpt-5.6-terra" or "moonshot/kimi-k3".
  // Render short: "<displayName> · <short-model>".
  const shortModel = model ? String(model).split("/").pop() : null;
  return (
    <div className="px-3 py-2 flex items-center gap-2 flex-wrap">
      <span
        data-testid="ai-card-header-label"
        data-model={model || ""}
        className="mono text-[11px] uppercase tracking-widest text-slate-200 font-bold"
      >
        {displayName}
        {shortModel && (
          <span className="text-slate-500 font-normal normal-case tracking-normal">
            {" · "}{shortModel}
          </span>
        )}
      </span>
      {stance && <StancePill stance={stance} />}
      <div className="ml-auto flex items-center gap-2">
        {cached && <span className="mono text-[9px] uppercase tracking-widest text-slate-500">cached</span>}
        {latency != null && !cached && (
          <span className="mono text-[9px] uppercase tracking-widest text-slate-500 tabular-nums">
            {typeof latency === "number" ? `${latency.toFixed(1)}s` : latency}
          </span>
        )}
      </div>
    </div>
  );
}

function FactorBlock({ title, items, tone, testid }) {
  const cls = {
    emerald: "text-emerald-300 border-emerald-500/20 bg-emerald-500/5",
    red:     "text-red-300     border-red-500/20     bg-red-500/5",
    amber:   "text-amber-300   border-amber-500/20   bg-amber-500/5",
    slate:   "text-slate-300   border-slate-500/20   bg-slate-500/5",
  }[tone];
  return (
    <div data-testid={testid} className={`border ${cls} px-3 py-2`}>
      <div className="mono text-[10px] uppercase tracking-widest opacity-80 mb-1">{title}</div>
      {(!items || items.length === 0) ? (
        <div className="mono text-[10px] text-slate-500 italic">— none —</div>
      ) : (
        <ul className="space-y-1">
          {items.map((it, i) => (
            <li key={i} className="text-[12px] leading-snug flex gap-1">
              <span className="opacity-60 shrink-0">▸</span>
              <span>{it}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TextBlock({ title, body, testid }) {
  return (
    <div data-testid={testid} className="border border-[#1E293B] bg-[#0F1626] px-3 py-2">
      <div className="mono text-[10px] uppercase tracking-widest text-cyan-400 mb-1">{title}</div>
      <p className="text-slate-300 text-[12px] leading-snug">{body || "—"}</p>
    </div>
  );
}
